/**
 * Utility to extract US state abbreviations from payer addresses
 * and filter payers by provider's licensed states.
 */

const US_STATES = new Set([
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  'DC'
]);

// National carriers that should always appear regardless of state
const NATIONAL_CARRIER_KEYWORDS = [
  'united healthcare', 'unitedhealthcare', 'unitedhealth',
  'aetna',
  'cigna',
  'humana',
  'anthem',
  'tricare',
  'medicare',
  'medicaid',
  'magellan',
  'optum',
  'beacon health',
  'carelon',
];

/**
 * Extract state abbreviation from a payer address string.
 * Addresses typically end with "City, ST ZIP" format.
 */
export function getStateFromAddress(address: string): string | null {
  if (!address) return null;

  // Match pattern: ", ST " or ", ST-" or end with ", ST ZIP"
  // Look for 2-letter state abbreviation after a comma+space, before a space+ZIP or end
  const match = address.match(/,\s*([A-Z]{2})\s+\d{5}/);
  if (match && US_STATES.has(match[1])) {
    return match[1];
  }

  // Fallback: look for ", ST" at end of string or before hyphenated ZIP
  const fallback = address.match(/,\s*([A-Z]{2})\s*$/);
  if (fallback && US_STATES.has(fallback[1])) {
    return fallback[1];
  }

  // Check for state name in the payer name itself (e.g., "Blue Cross Blue Shield of Texas")
  return null;
}

/**
 * Check if a payer name contains a state reference (e.g., "BCBS of Texas", "CareSource of Georgia")
 */
export function getStateFromPayerName(name: string): string[] {
  const stateNameMap: Record<string, string> = {
    'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
    'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
    'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID',
    'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS',
    'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
    'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS',
    'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV',
    'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
    'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK',
    'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
    'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT',
    'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV',
    'wisconsin': 'WI', 'wyoming': 'WY',
  };

  const lowerName = name.toLowerCase();
  const states: string[] = [];

  for (const [stateName, abbrev] of Object.entries(stateNameMap)) {
    if (lowerName.includes(stateName)) {
      // Avoid false positive: "Virginia" matching inside "West Virginia"
      if (stateName === 'virginia' && lowerName.includes('west virginia')) continue;
      states.push(abbrev);
    }
  }

  return states;
}

/**
 * Check if a payer is a national carrier (should show for all states)
 */
export function isNationalCarrier(name: string): boolean {
  const lowerName = name.toLowerCase();
  return NATIONAL_CARRIER_KEYWORDS.some(keyword => lowerName.includes(keyword));
}

/**
 * Get licensed states from provider profile data.
 * Accepts an optional profile object with a licenses array.
 * Falls back to localStorage for backward compatibility.
 */
export function getLicensedStates(profile?: { licenses?: Array<{ state?: string }> } | null): string[] {
  try {
    const src = profile ?? (() => {
      const stored = localStorage.getItem('providerProfile');
      return stored ? JSON.parse(stored) : null;
    })();
    if (!src || !src.licenses || !Array.isArray(src.licenses)) return [];
    const states = src.licenses
      .map((l: { state?: string }) => l.state?.toUpperCase().trim())
      .filter((s: string | undefined): s is string => !!s && US_STATES.has(s));
    return Array.from(new Set(states));
  } catch {
    return [];
  }
}

/**
 * Determine if a payer is relevant to the given set of states.
 * A payer is relevant if:
 * 1. Its address is in one of the states, OR
 * 2. Its name references one of the states, OR
 * 3. It's a national carrier
 */
export function isPayerRelevantToStates(
  payer: { name: string; address: string },
  states: string[]
): boolean {
  if (states.length === 0) return false;

  // National carriers are always relevant
  if (isNationalCarrier(payer.name)) return true;

  // Check address state
  const addressState = getStateFromAddress(payer.address);
  if (addressState && states.includes(addressState)) return true;

  // Check name for state references
  const nameStates = getStateFromPayerName(payer.name);
  if (nameStates.some(s => states.includes(s))) return true;

  return false;
}
